import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { AlertTriangle } from "lucide-react";

/**
 * ConfirmDeleteModal — double-confirm dialog for destructive actions.
 * User must type "HAPUS" and optionally provide a reason before delete.
 * onConfirm receives { reason }.
 */
export default function ConfirmDeleteModal({ open, onOpenChange, title, message, itemLabel, onConfirm, requireReason = false }) {
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const canConfirm = typed.trim().toUpperCase() === "HAPUS" && (!requireReason || reason.trim().length > 3);

  const submit = async () => {
    if (!canConfirm) return;
    setBusy(true);
    try { await onConfirm({ reason: reason.trim() }); setTyped(""); setReason(""); onOpenChange(false); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { setTyped(""); setReason(""); onOpenChange(v); }}}>
      <DialogContent data-testid="confirm-delete-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 text-rose-700"><AlertTriangle className="w-5 h-5"/>
            <DialogTitle>{title || "Konfirmasi Penghapusan"}</DialogTitle>
          </div>
        </DialogHeader>
        <div className="text-sm text-[#5C6066] py-2">
          {message || "Tindakan ini akan menghapus data secara permanen (setelah 30 hari)."}
          {itemLabel && <div className="mt-2 p-2 bg-rose-50 border border-rose-200 rounded font-mono text-xs text-rose-800">{itemLabel}</div>}
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Ketik <b className="text-rose-700">HAPUS</b> untuk konfirmasi</Label>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="HAPUS" className="mt-1.5 font-mono uppercase" data-testid="confirm-delete-input"/>
          </div>
          {requireReason && (
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Alasan penghapusan {requireReason && "*"}</Label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5" data-testid="confirm-delete-reason"/>
            </div>
          )}
          <div className="text-[11px] text-[#8C9096] bg-amber-50 border border-amber-200 p-2 rounded">
            Data akan masuk Recycle Bin dan bisa direstore selama 30 hari oleh Owner.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Batal</Button>
          <Button variant="destructive" onClick={submit} disabled={!canConfirm || busy} data-testid="confirm-delete-submit">
            {busy ? "Menghapus..." : "Hapus Permanen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
